// Console D's tables: `.t` (12.5 px mono, 34 px rows) inside a region that scrolls sideways on a
// phone. j and k move between rows (lib/keys.ts useRowKeys); a row that opens (`hov`) does so on
// a click and on Enter. `cur` is the view's one accent: the row this page is about.
import { type KeyboardEvent, type ReactNode, useRef } from 'react'
import { useRowKeys } from '../keys'

export function Table({
  label,
  rowKeys,
  className,
  children,
}: {
  /** What the table lists, for the scroll region's name. */
  label: string
  /** j/k move between the rows. One table a page. */
  rowKeys?: boolean
  className?: string
  children: ReactNode
}) {
  const ref = useRef<HTMLTableElement>(null)
  useRowKeys(rowKeys ? ref : NONE)
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable
    <section className="scroll-x" aria-label={label} tabIndex={0}>
      <table
        ref={ref}
        className={className ? `t ${className}` : 't'}
        data-row-keys={rowKeys ? '' : undefined}
      >
        {children}
      </table>
    </section>
  )
}

const NONE = { current: null }

/**
 * A row that opens something: hover shows it, Enter or a click opens it. When the row holds a
 * link, j/k focus that link; otherwise the row itself takes focus.
 */
export function HovRow({
  onOpen,
  cur,
  label,
  className,
  children,
}: {
  onOpen: () => void
  cur?: boolean
  /** What opening the row does, for a screen reader on the focused row. */
  label?: string
  /** More classes, for example `dim`. */
  className?: string | undefined
  children: ReactNode
}) {
  const onKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' && e.target === e.currentTarget) {
      e.preventDefault()
      onOpen()
    }
  }
  return (
    <tr
      className={[cur ? 'hov cur' : 'hov', className].filter(Boolean).join(' ')}
      tabIndex={0}
      aria-label={label}
      aria-current={cur ? 'true' : undefined}
      onClick={(e) => {
        // A link or a button in the row keeps its own click.
        if ((e.target as HTMLElement).closest('a, button, input, select')) return
        onOpen()
      }}
      onKeyDown={onKeyDown}
    >
      {children}
    </tr>
  )
}

/** A sortable column head: the current sort reads ↓, the others ↕. */
export function SortTh({
  on,
  onSort,
  className,
  children,
}: {
  on: boolean
  onSort: () => void
  className?: string
  children: ReactNode
}) {
  return (
    <th
      className={['sort', on ? 'on' : '', className ?? ''].filter(Boolean).join(' ')}
      aria-sort={on ? 'descending' : 'none'}
    >
      <button type="button" onClick={onSort}>
        {children}
      </button>
    </th>
  )
}

/** The line under a table: what it counts, the keys, the next page. */
export function TableFoot({ end, children }: { end?: boolean; children: ReactNode }) {
  return <div className={end ? 't-foot end' : 't-foot'}>{children}</div>
}

/** A wide block that scrolls inside the page (a table, a chart), focusable so keys reach it. */
export function ScrollX({ label, children }: { label: string; children: ReactNode }) {
  return (
    // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable
    <section className="scroll-x" aria-label={label} tabIndex={0}>
      {children}
    </section>
  )
}
